import React from 'react'
import { router } from '@inertiajs/react'
import Layout from '../Layout'
import DataTable from '../../../Components/DataTable'
import Button from '../../../Components/Button'
import ResourceIndexHeader from '../../../Components/ResourceIndexHeader'
import FilterPills from '../../../Components/FilterPills'
import { useDeleteWithConfirmation } from '../../../hooks/useDeleteWithConfirmation'

export default function Index({ account, accounts, navigation, print_orders, statuses, params = {}, pagination }) {
  const handleDelete = useDeleteWithConfirmation('/manage/print_orders')

  const handleStatusFilter = (status) => {
    router.visit('/manage/print_orders', {
      data: { ...params, status: status || undefined },
      preserveState: true
    })
  }

  const columns = [
    {
      key: 'date',
      label: 'Desired Delivery Date',
      sortable: true,
      render: (value) => value ? new Date(value).toLocaleDateString() : '\u2014'
    },
    {
      key: 'print_asset_name',
      label: 'Print Asset'
    },
    {
      key: 'position_count',
      label: 'Locations',
      render: (value) => `${value} location${value !== 1 ? 's' : ''}`
    },
    {
      key: 'quantity',
      label: 'Qty'
    },
    {
      key: 'status_label',
      label: 'Status',
      sortable: true,
      render: (value, item) => {
        const colours = {
          draft: 'bg-gray-100 text-gray-800',
          confirmed: 'bg-blue-100 text-blue-800',
          ready: 'bg-green-100 text-green-800',
          cancelled: 'bg-red-100 text-red-800',
          completed: 'bg-emerald-100 text-emerald-800',
          failed: 'bg-red-100 text-red-800'
        }
        return (
          <span
            className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${colours[item.status] || colours.draft}`}
            title={item.is_failed ? item.error_message : undefined}
          >
            {value}
          </span>
        )
      }
    }
  ]

  return (
    <Layout account={account} accounts={accounts} navigation={navigation} title="Print Orders">
      <ResourceIndexHeader
        count={pagination ? pagination.count : print_orders.length}
        singular="print order"
        plural="print orders"
        addHref="/manage/print_orders/new"
        addLabel="New Print Order"
        actions={
          <Button href="/manage/print_assets" variant="secondary">
            Manage Assets
          </Button>
        }
      />

      <div className="mb-4">
        <FilterPills
          items={statuses}
          value={params.status || ''}
          onChange={handleStatusFilter}
        />
      </div>

      <DataTable
        data={print_orders}
        columns={columns}
        actions={(item) => (
          <>
            {item.has_pdf && (
              <a href={`/manage/print_orders/${item.id}/download_pdf`} className="inline-flex items-center font-medium transition focus:outline-none text-sm text-sky-600 hover:text-sky-500">
                Download PDF
              </a>
            )}
            {item.is_draft && (
              <>
                <Button link onClick={() => router.post(`/manage/print_orders/${item.id}/generate_pdf`)}>
                  Generate PDF
                </Button>
                <Button link href={`/manage/print_orders/${item.id}/edit`}>Edit</Button>
                <Button link variant="danger" onClick={() => {
                  if (window.confirm('Are you sure you want to cancel this print order?')) {
                    router.post(`/manage/print_orders/${item.id}/cancel`)
                  }
                }}>Cancel</Button>
                <Button link variant="danger" onClick={() => handleDelete(item, { nameField: 'print_asset_name' })}>Delete</Button>
              </>
            )}
            {item.is_failed && (
              <>
                <Button link onClick={() => router.post(`/manage/print_orders/${item.id}/generate_pdf`)}>
                  Retry
                </Button>
                <Button link href={`/manage/print_orders/${item.id}/edit`}>Edit</Button>
                <Button link variant="danger" onClick={() => handleDelete(item, { nameField: 'print_asset_name' })}>Delete</Button>
              </>
            )}
          </>
        )}
        emptyState={{
          message: 'No print orders yet',
          action: { label: 'Create your first print order', href: '/manage/print_orders/new' }
        }}
        baseUrl="/manage/print_orders"
        params={params}
        pagination={pagination}
      />
    </Layout>
  )
}
